import { isSearchValid } from '@/features/landing/components/search-bar';
import { SEARCH_MIN_CHARS } from '@/features/landing/constants';
import {
  AutocompleteResponse,
  AutocompleteSearchRequest,
} from '@/features/landing/landing-search.types';
import { TERM_AUTO_COMPLETE_URL } from '@/lib/constants';
import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';

const QUERY_METHOD = 'GET';
const CACHE_DURATION = 15 * 60 * 1000; // Cache data for 15 minutes
const RETRY_TIMES = 3;

async function autoCompleteSearch(
  params: AutocompleteSearchRequest,
  signal: AbortSignal | undefined
): Promise<AutocompleteResponse> {
  const { input, coid, program, LocationLatitude, LocationLongitude, ...props } = params;

  const queryParams = new URLSearchParams({
    input,
    coid,
    program,
    ...(LocationLatitude && { LocationLatitude }),
    ...(LocationLongitude && { LocationLongitude }),
    ...props,
  });
  const url = `${TERM_AUTO_COMPLETE_URL}?${queryParams.toString()}`;
  const requestOptions = {
    method: QUERY_METHOD,
    redirect: 'follow' as RequestRedirect,
    signal,
  };
  const response = await fetch(url, requestOptions);
  if (!response.ok) {
    throw new Error(`Failed to fetch OAS providers' data: ${response.statusText}`);
  }
  return response.json();
}

// [OAS_QUERY_KEY, params.input, params.coid, params.program];
const OAS_QUERY_KEY = 'OAS_SEARCH';
// const QueryKeys = {
//   all: ['OAS_SEARCH'] as const,
//   list: (filters: ListingRequest) => ['OAS_SEARCH', filters] as const,
// };

export const useTermAutoComplete = (
  params: AutocompleteSearchRequest,
  options?: Omit<UseQueryOptions<AutocompleteResponse, Error>, 'queryFn' | 'queryKey'>
): UseQueryResult<AutocompleteResponse> => {
  const oasResponseData = useQuery<AutocompleteResponse, Error>({
    queryKey: [OAS_QUERY_KEY, params.input, params.coid, params.program, params.LocationUrlTitle],
    queryFn: ({ signal }) => autoCompleteSearch(params, signal),
    refetchOnWindowFocus: false,
    enabled:
      !!params.input &&
      params.input?.length >= SEARCH_MIN_CHARS &&
      !!params.coid &&
      !!params.program &&
      isSearchValid(params.input),
    refetchOnMount: true,
    staleTime: CACHE_DURATION,
    retry: RETRY_TIMES,
    select: (data) => {
      const { providers, specialties, locations, visibleTerms } = data.data;

      // This is done as requirement for categories are ordered in dropdown.
      return {
        ...data,
        data: { providers, specialties, visibleTerms, locations },
      };
    },
    ...options,
  });

  return oasResponseData;
};
